<template>
    <div class="card">
        <div class="card-header d-flex justify-content-between align-items-start">
            <h5 class="card-title">Additional Charges</h5>
        </div>
        <ul class="list-group list-group-flush" v-if="!currentNonProductionCharges || !currentNonProductionCharges.length">
            <li class="list-group-item">
                The list is currently empty
            </li>
        </ul>
        <b-table striped hover :items="currentNonProductionCharges" :fields="tableFields" class="mb-0" :tbody-tr-class="rowClass" v-if="currentNonProductionCharges && currentNonProductionCharges.length">
            <template v-slot:row-details="row">
                <b-card body-class="p-2">
                    <b-form-group class="mb-0"
                                  label-class="font-weight-bold mb-0"
                                  label="Related To Dates:"
                                  label-for="relatedToDates"
                                  v-if="(row.item.relatedToDates && row.item.relatedToDates.length) || row.item.relatedToAllDates">
                        <b-form-input name="relatedToDates" type="text" plaintext :value="relatedToDatesText(row.item.relatedToDates, row.item.relatedToAllDates)" />
                    </b-form-group>
                    <b-form-group class="mb-0"
                                  label-class="font-weight-bold mb-0"
                                  label="Comments:"
                                  label-for="comments"
                                  v-if="row.item.comments">
                        <b-form-input name="comments" type="text" plaintext :value="row.item.comments" />
                    </b-form-group>
                </b-card>
            </template>
            <template v-slot:cell(actions)="row">
                <b-button-group size="sm">
                    <b-button key="edit"
                              variant="outline-secondary"
                              v-on:click="editNonProductionCharge(row.item)"
                              v-if="!readonly">
                        <i class="fas fa-edit mr-1"></i>Edit
                    </b-button>
                    <b-button key="remove"
                              variant="outline-danger"
                              v-on:click="removeNonProductionCharge(row.item)"
                              v-if="!readonly">
                        <i class="fas fa-times mr-1"></i>Remove
                    </b-button>
                    <b-button key="toggledetails"
                              v-on:click="row.toggleDetails"
                              v-if="(row.item.relatedToDates && row.item.relatedToDates.length) || row.item.relatedToAllDates || row.item.comments">
                        <template v-if="!row.detailsShowing">
                            <i class="fas fa-caret-down mr-1" key="showdetails"></i>Show
                        </template>
                        <template v-else>
                            <i class="fas fa-caret-up mr-1" key="hidedetails"></i>Hide
                        </template>
                        &nbsp;Details
                    </b-button>
                </b-button-group>
            </template>
        </b-table>
        <ul class="list-group list-group-flush" v-if="!readonly">
            <li class="list-group-item">
                <!-- the disabled propery is important because autherwise the observer and all providers will be part of a parent validation, which we do not want, validation only has to occur when using the add/update button-->
                <validation-observer ref="nonProductionChargeValidator" v-slot="{ passes }" slim disabled>
                    <b-form-row>
                        <validation-provider rules="required" name="Charge Type" v-slot="{ valid, errors }" slim>
                            <b-form-group label="Charge Type"
                                          label-for="nonProductionChargeType"
                                          class="col-md-4">
                                <select2 name="nonProductionChargeType"
                                         placeholder="Select a charge type"
                                         v-model="currentNonProductionCharge.nonProductionChargeTypeId"
                                         :options="allNonProductionChargeTypes"
                                         key-property="nonProductionChargeTypeId"
                                         text-property="name"
                                         disable-search
                                         :state="errors[0] ? false : null"
                                         @select="nonProductionChargeTypeSelected">
                                </select2>
                                <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        <validation-provider :rules="{ required: quantityRequired, integer: true }" name="Quantity" v-slot="{ errors }" slim v-if="quantityOptional || quantityRequired">
                            <b-form-group :label="currentNonProductionCharge.nonProductionChargeTypeUnit ? 'Quantity (' + currentNonProductionCharge.nonProductionChargeTypeUnit + ')' : 'Quantity'"
                                          label-for="quantity"
                                          class="col-md-2">
                                <b-form-input name="quantity"
                                              type="number"
                                              step="1"
                                              v-model="currentNonProductionCharge.quantity"
                                              :state="errors[0] ? false : null">
                                </b-form-input>
                                <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        <validation-provider :rules="{ required: unitCostRequired }" name="Charge/Unit" v-slot="{ errors }" slim v-if="unitCostOptional || unitCostRequired">
                            <b-form-group label="Unit Cost"
                                          label-for="unitCost"
                                          class="col-md-2">
                                <b-form-input name="unitCost"
                                              type="number"
                                              step="0.01"
                                              v-model="currentNonProductionCharge.unitCost"
                                              :state="errors[0] ? false : null">
                                </b-form-input>
                                <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        <validation-provider :rules="{ required: totalCostRequired }" name="Total Charge" v-slot="{ errors }" slim v-if="totalCostOptional || totalCostRequired">
                            <b-form-group label="Total Cost"
                                          label-for="totalCost"
                                          class="col-md-2">
                                <b-form-input name="totalCost"
                                              type="number"
                                              step="0.01"
                                              v-model="currentNonProductionCharge.totalCost"
                                              :state="errors[0] ? false : null">
                                </b-form-input>
                                <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>

                        <b-form-group label="Chargeable"
                                      label-for="chargeable"
                                      class="col-md-1"
                                      v-if="selectedNonProductionChargeType">
                            <b-form-checkbox name="chargeable"
                                             v-model="currentNonProductionCharge.chargeable"
                                             class="mb-2 text"
                                             switch>
                            </b-form-checkbox>
                        </b-form-group>

                       

                    </b-form-row>


                    <!--<b-form-row v-if="selectedNonProductionChargeType" class="">
                        <b-form-checkbox name="splitCosts"
                                         v-model="currentNonProductionCharge.splitCosts"
                                         @change="splitCostsChanged()">
                        </b-form-checkbox>Split costs ?
                    </b-form-row>-->

                    <!-- split costs row -->
                    <b-form-row class="p-1" v-if="selectedNonProductionChargeType" >

                        <b-form-group label="Split costs"
                                      label-for="splitCosts"
                                      class="col-md-1 bg-light">
                            <b-form-checkbox name="splitCosts"
                                             v-model="currentNonProductionCharge.splitCosts"
                                             @change="splitCostsChanged()"
                                             class="mb-2"
                                             switch>
                            </b-form-checkbox>
                        </b-form-group>

                        <validation-provider ref="percentageValidationProvider"
                                             :rules="{ min_value:1, max_value:99, required_if: (selectedNonProductionChargeType != null && currentNonProductionCharge.splitCosts == true) }"
                                             name="Percentage"
                                             class="col-md-1"
                                             v-slot="{ validate, errors }"
                                             slim>
                            <b-form-group label="Percentage"
                                          label-for="percentage"
                                          class="bg-light">
                                <b-form-input name="percentage"
                                              type="number"
                                              number
                                              :min="1"
                                              :max="99"
                                              v-model="currentNonProductionCharge.splitPercentage"
                                              :disabled="!currentNonProductionCharge.splitCosts"
                                              :state="errors[0] ? false : null">
                                </b-form-input>
                                <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider :rules="{ required: (relatedToDatesRequired && !currentNonProductionCharge.relatedToAllDates) }" name="Related To Dates" v-slot="{ errors }" slim v-if="relatedToDatesOptional || relatedToDatesRequired">
                            <b-form-group label="Related to dates"
                                          label-for="relatedToDates"
                                          class="col-md-8">
                                <select2 name="nonProductionChargeType"
                                         :placeholder="currentNonProductionCharge.relatedToAllDates ? 'All dates' : 'Select related dates'"
                                         v-model="currentNonProductionCharge.relatedToDates"
                                         :options="allPossibleDates"
                                         key-property="date"
                                         multiple
                                         disable-search
                                         :disabled="currentNonProductionCharge.relatedToAllDates"
                                         :state="errors[0] ? false : null">
                                </select2>
                                <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>

                        <b-form-group label="All dates"
                                      label-for="relatedToAllDates"
                                      class="col-md-1"
                                      v-if="relatedToDatesOptional || relatedToDatesRequired">
                            <b-form-checkbox name="relatedToAllDates"
                                             v-model="currentNonProductionCharge.relatedToAllDates"
                                             switch
                                             @change="relatedToAllDatesChanged">
                            </b-form-checkbox>
                        </b-form-group>

                    </b-form-row>

                    <!-- comment row -->
                    <b-form-row>
                        <b-form-group label="Comments"
                                      label-for="comments"
                                      class="col-md-12">
                            <b-form-textarea name="comments"
                                             rows="3"
                                             v-model="currentNonProductionCharge.comments">
                            </b-form-textarea>
                        </b-form-group>
                    </b-form-row>

                    <b-form-group label="Action"
                                  class="col-md-2 pl-3">
                        <b-button-group>
                            <b-button variant="outline-secondary" v-on:click.prevent="passes(saveNonProductionCharge)"><i class="fas fa-plus mr-1"></i>{{ (currentEditPosition == null) ? 'Add' : 'Update' }}</b-button>
                            <b-button variant="outline-danger" v-on:click="resetNonProductionCharge"><i class="fas fa-times mr-1"></i>{{ (currentEditPosition == null) ? 'Reset' : 'Cancel' }}</b-button>
                        </b-button-group>
                    </b-form-group>

                </validation-observer>
            </li>
        </ul>
    </div>
</template>

<script>
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    
    import { ValidationProvider, ValidationObserver } from 'vee-validate';
    import { extend, localize } from 'vee-validate';
    import { required, integer } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    extend('required', required);
    extend('integer', integer);
    localize('en', en);
    
    import ErrorsMixin from '../mixins/ErrorsMixin';
    import DateTimeMixin from '../mixins/DateTimeMixin';
    import Select2 from './Select2';

    Vue.use(BootstrapVue);
    
    export default {
        name: 'NonProductionCharges',
        mixins: [ErrorsMixin, DateTimeMixin],
        components: {
            'validation-provider': ValidationProvider,
            'validation-observer': ValidationObserver,
            'select2': Select2
        },
        model: {
            event: 'change',
            prop: 'nonProductionCharges'
        },
        props: {
            allNonProductionChargeTypes: {
                type: Array,
                default: null
            },
            allPossibleDates: {
                type: Array,
                default: null
            },
            useClientRate: {
                type: Boolean,
                default: false
            },
            nonProductionCharges: {
                type: Array,
                default: null
            },
            readonly: {
                type: Boolean,
                default: false
            }
        },
        data: function () {
            return {
                selectedNonProductionChargeType: null,
                currentNonProductionCharges: this.nonProductionCharges,
                currentEditPosition: null,
                currentNonProductionCharge: {
                    nonProductionChargeId: null,
                    nonProductionChargeTypeId: null,
                    nonProductionChargeTypeName: null,
                    nonProductionChargeTypeUnit: null,
                    quantity: null,
                    splitCosts: false,
                    splitPercentage: null,
                    unitCost: null,
                    totalCost: null,
                    chargeable: false,
                    comments: null,
                    relatedToDates: null,
                    relatedToAllDates: false
                },
                currentErrors: [],
                tableFields: [
                    {
                        key: 'nonProductionChargeTypeName',
                        label: 'Charge Type'
                    },
                    {
                        key: 'quantity',
                        label: 'Quantity',
                        class: 'fit text-right',
                        formatter: (value, key, item) => {
                            if ((value != null) && (item.nonProductionChargeTypeUnit != null)) {
                                return value + ' ' + item.nonProductionChargeTypeUnit;
                            }
                            return value;
                        }
                    },
                    {
                        key: 'splitPercentage',
                        label: 'Split %',
                        class: 'fit text-right',
                        formatter: (value) => {
                            if (value != null) return value.toFixed(2);
                        }
                    },
                    {
                        key: 'unitCost',
                        label: 'Unit Cost',
                        class: 'fit text-right',
                        formatter: (value) => {
                            if (value != null) return value.toFixed(2);
                        }
                    },
                    {
                        key: 'totalCost',
                        label: 'Total Cost',
                        class: 'fit text-right',
                        formatter: (value, key, item) => {
                            if (value != null) return value.toFixed(2);
                            if ((item.quantity != null) && (item.unitCost != null) && (item.splitPercentage == null)) return (item.quantity * item.unitCost).toFixed(2);
                            if ((item.quantity != null) && (item.unitCost != null) && (item.splitPercentage != null)) return (((item.quantity * item.unitCost) * item.splitPercentage)/100).toFixed(2);
                        }
                    },
                    {
                        key: 'chargeable',
                        label: 'Chargeable',
                        class: 'fit text-center',
                        formatter: (value) => {
                            if (value === true) return 'Yes';
                            if (value === false) return 'No';
                        }
                    },
                    {
                        key: 'actions',
                        label: 'Actions',
                        class: 'fit'
                    }
                ],
            };
        },
        watch: {
            allPossibleDates: function () {
                var changed = false;
                if (this.currentNonProductionCharges) {
                    this.currentNonProductionCharges.forEach((nonProductionCharge) => {
                        if (nonProductionCharge.relatedToDates) {
                            var i = 0;
                            //var dateToCheck = nonProductionCharge.relatedToDates[i]
                            while (i < nonProductionCharge.relatedToDates.length) {
                                if (!this.allPossibleDates.find(dateEntry => dateEntry.date == nonProductionCharge.relatedToDates[i])) {
                                    nonProductionCharge.relatedToDates.splice(i, 1);
                                    changed = true;                                    
                                } else {
                                    i++;
                                }
                            }

                            //TOPSDEV-3622 If the date is included in the CourtQuoteLineItemSittingDayAssignments then add the sitting date back into the list
                            if (nonProductionCharge.courtQuoteLineItemSittingDayAssignments) {
                                nonProductionCharge.courtQuoteLineItemSittingDayAssignments.forEach((quoteSittingDay) => {
                                    this.allPossibleDates.forEach((sittingDay) => {
                                        if (this.formatDateTime(sittingDay.date, 'DD/MM/YYYY') == this.formatDateTime(quoteSittingDay, 'DD/MM/YYYY')) {
                                            if (!nonProductionCharge.relatedToDates.find(dateToCheck => dateToCheck == sittingDay.date)) {
                                                nonProductionCharge.relatedToDates.push(sittingDay.date);
                                            }
                                        }
                                        //console.log('sittingDay ' + this.formatDateTime(sittingDay.date, 'DD/MM/YYYY'));
                                        //console.log('quoteSittingDay ' + this.formatDateTime(quoteSittingDay, 'DD/MM/YYYY'));
                                    });
                                });
                            }                          
                        }
                    });
                }
                if (changed) {
                    this.$emit('change', this.currentNonProductionCharges);
                }
            }
        },
        computed: {
            quantityOptional: function () {
                if (!this.selectedNonProductionChargeType) return false;
                return (this.selectedNonProductionChargeType.optionsList.indexOf(1024) >= 0);
            },
            quantityRequired: function () {
                if (!this.selectedNonProductionChargeType) return false;
                return (this.selectedNonProductionChargeType.optionsList.indexOf(1) >= 0);
            },
            unitCostOptional: function () {
                if (!this.selectedNonProductionChargeType) return false;
                return (this.selectedNonProductionChargeType.optionsList.indexOf(2048) >= 0);
            },
            unitCostRequired: function () {
                if (!this.selectedNonProductionChargeType) return false;
                return (this.selectedNonProductionChargeType.optionsList.indexOf(2) >= 0);
            },
            totalCostOptional: function () {
                if (!this.selectedNonProductionChargeType) return false;
                return (this.selectedNonProductionChargeType.optionsList.indexOf(4096) >= 0);
            },
            totalCostRequired: function () {
                if (!this.selectedNonProductionChargeType) return false;
                return (this.selectedNonProductionChargeType.optionsList.indexOf(4) >= 0);
            },
            relatedToDatesOptional: function () {
                if (!this.selectedNonProductionChargeType) return false;
                return (this.selectedNonProductionChargeType.optionsList.indexOf(8192) >= 0);
            },
            relatedToDatesRequired: function () {
                if (!this.selectedNonProductionChargeType) return false;
                return (this.selectedNonProductionChargeType.optionsList.indexOf(8) >= 0);
            }
        },
        methods: {
            rowClass: function (nonProductionCharge, type) {
                if (!nonProductionCharge || (type !== 'row')) return;
                if (this.currentEditPosition == null) return;
                var position = this.currentNonProductionCharges.indexOf(nonProductionCharge);
                if (this.currentEditPosition == position) return 'table-success';
            },
            relatedToDatesText(relatedToDates, relatedToAllDates) {
                if (relatedToDates && relatedToDates.length) {
                    var relatedToDatesText = '';
                    relatedToDates.forEach((date) => {
                        if (relatedToDatesText != '') relatedToDatesText += ', ';
                        relatedToDatesText += this.formatDateTime(date, 'DD/MM/YYYY', true, true);
                    });
                    return relatedToDatesText;
                } else if (relatedToAllDates) {
                    return "All Dates";
                }
                return null;
            },
            nonProductionChargeTypeSelected: function (nonProductionChargeType, setUnitCost = true) {
                this.selectedNonProductionChargeType = nonProductionChargeType;
                if (nonProductionChargeType) {
                    this.currentNonProductionCharge.nonProductionChargeTypeName = nonProductionChargeType.name;
                    this.currentNonProductionCharge.nonProductionChargeTypeUnit = nonProductionChargeType.unit;
                    if (setUnitCost && (this.unitCostOptional || this.unitCostRequired)) {
                        this.currentNonProductionCharge.unitCost = this.useClientRate ? nonProductionChargeType.clientRate : nonProductionChargeType.externalRate;
                    }
                }
                else {
                    this.currentNonProductionCharge.nonProductionChargeTypeName = null;
                    this.currentNonProductionCharge.nonProductionChargeTypeUnit = null;
                    if (setUnitCost) {
                        this.currentNonProductionCharge.unitCost = null;
                    }
                }
            },
            relatedToAllDatesChanged: function (relatedToAllDates) {
                if (this.currentNonProductionCharge && relatedToAllDates) this.currentNonProductionCharge.relatedToDates = [];
            },
            saveNonProductionCharge: function () {
                if (this.currentEditPosition != null) {
                    this.currentNonProductionCharges[this.currentEditPosition].nonProductionChargeTypeId = this.currentNonProductionCharge.nonProductionChargeTypeId;
                    this.currentNonProductionCharges[this.currentEditPosition].nonProductionChargeTypeName = this.currentNonProductionCharge.nonProductionChargeTypeName;
                    this.currentNonProductionCharges[this.currentEditPosition].nonProductionChargeTypeUnit = this.currentNonProductionCharge.nonProductionChargeTypeUnit;
                    this.currentNonProductionCharges[this.currentEditPosition].quantity = (this.currentNonProductionCharge.quantity == null) ? null : parseFloat(this.currentNonProductionCharge.quantity);
                    this.currentNonProductionCharges[this.currentEditPosition].splitPercentage = (!this.currentNonProductionCharge || this.currentNonProductionCharge.splitPercentage == null) ? null : parseFloat(this.currentNonProductionCharge.splitPercentage);
                    this.currentNonProductionCharges[this.currentEditPosition].unitCost = (this.currentNonProductionCharge.unitCost == null) ? null : parseFloat(this.currentNonProductionCharge.unitCost);
                    this.currentNonProductionCharges[this.currentEditPosition].totalCost = (this.currentNonProductionCharge.totalCost == null) ? null : parseFloat(this.currentNonProductionCharge.totalCost);
                    this.currentNonProductionCharges[this.currentEditPosition].chargeable = this.currentNonProductionCharge.chargeable;
                    this.currentNonProductionCharges[this.currentEditPosition].comments = this.currentNonProductionCharge.comments;
                    this.currentNonProductionCharges[this.currentEditPosition].relatedToDates = this.currentNonProductionCharge.relatedToDates;
                    this.currentNonProductionCharges[this.currentEditPosition].relatedToAllDates = this.currentNonProductionCharge.relatedToAllDates;
                } else {
                    if (this.currentNonProductionCharges == null) this.currentNonProductionCharges = [];
                    if (this.currentNonProductionCharge.quantity != null) this.currentNonProductionCharge.quantity = parseFloat(this.currentNonProductionCharge.quantity);
                    if (this.currentNonProductionCharge && this.currentNonProductionCharge.splitPercentage != null) this.currentNonProductionCharge.splitPercentage = parseFloat(this.currentNonProductionCharge.splitPercentage);
                    if (this.currentNonProductionCharge.unitCost != null) this.currentNonProductionCharge.unitCost = parseFloat(this.currentNonProductionCharge.unitCost);
                    if (this.currentNonProductionCharge.totalCost != null) this.currentNonProductionCharge.totalCost = parseFloat(this.currentNonProductionCharge.totalCost);
                    this.currentNonProductionCharges.push(this.currentNonProductionCharge);
                }
                this.currentEditPosition = null;
                this.currentNonProductionCharge = {
                    nonProductionChargeId: null,
                    nonProductionChargeTypeId: null,
                    nonProductionChargeTypeName: null,
                    nonProductionChargeTypeUnit: null,
                    quantity: null,
                    splitCosts: false,
                    splitPercentage: null,
                    unitCost: null,
                    totalCost: null,
                    chargeable: false,
                    comments: null,
                    relatedToDates: null,
                    relatedToAllDates: false
                };
                this.nonProductionChargeTypeSelected(null);
                this.$refs.nonProductionChargeValidator.reset();
                this.$emit('change', this.currentNonProductionCharges);
            },
            resetNonProductionCharge: function () {
                this.currentEditPosition = null;
                this.currentNonProductionCharge = {
                    nonProductionChargeId: null,
                    nonProductionChargeTypeId: null,
                    nonProductionChargeTypeName: null,
                    quantity: null,
                    splitCosts: false,
                    splitPercentage: null,
                    unitCost: null,
                    totalCost: null,
                    chargeable: false,
                    comments: null,
                    relatedToDates: null,
                    relatedToAllDates: false
                };
                this.nonProductionChargeTypeSelected(null);
                this.$refs.nonProductionChargeValidator.reset();
            },
            editNonProductionCharge: function (nonProductionCharge) {
                this.currentEditPosition = this.currentNonProductionCharges.indexOf(nonProductionCharge);
                this.currentNonProductionCharge = {
                    nonProductionChargeId: nonProductionCharge.nonProductionChargeId,
                    nonProductionChargeTypeId: nonProductionCharge.nonProductionChargeTypeId,
                    nonProductionChargeTypeName: nonProductionCharge.nonProductionChargeTypeName,
                    nonProductionChargeTypeUnit: nonProductionCharge.nonProductionChargeTypeUnit,
                    quantity: nonProductionCharge.quantity,
                    splitCosts: (nonProductionCharge.splitPercentage != null ? true : false),
                    splitPercentage: nonProductionCharge.splitPercentage,
                    unitCost: nonProductionCharge.unitCost,
                    totalCost: nonProductionCharge.totalCost,
                    chargeable: nonProductionCharge.chargeable,
                    comments: nonProductionCharge.comments,
                    relatedToDates: nonProductionCharge.relatedToDates,
                    relatedToAllDates: nonProductionCharge.relatedToAllDates
                };
                var nonProductionChargeType = this.allNonProductionChargeTypes.find((nonProductionChargeType) => {
                    return (nonProductionChargeType.nonProductionChargeTypeId == nonProductionCharge.nonProductionChargeTypeId);
                });
                this.nonProductionChargeTypeSelected(nonProductionChargeType, false);
            },
            removeNonProductionCharge: function (nonProductionCharge) {
                var position = this.currentNonProductionCharges.indexOf(nonProductionCharge);
                if (position >= 0) {
                    this.currentNonProductionCharges.splice(position, 1);
                }
                this.$emit('change', this.currentNonProductionCharges);
            },
            splitCostsChanged: function () {
                if (!this.currentNonProductionCharge.splitCosts) {
                    this.currentNonProductionCharge.splitPercentage = null;
                }
            },
        }
    }
</script>